import React, { createContext, useEffect, useMemo, useState, useContext } from 'react';

const translations = {
  en: {
    nav: {
      explore: 'Explore',
      products: 'Products',
      why: 'Why Us',
      shop: 'Shop',
      brandTitle: 'Allo Soif',
      brandSub: 'Hand‑Selected Spirits',
      login: 'Login',
      logout: 'Logout',
      wishlist: 'Wishlist',
      cart: 'Cart'
    },
    landing: {
      heading: 'Your Journey Begins Here: Discover Hand‑Selected Spirits',
      sub: 'From artisanal distilleries to your door. Experience exceptional taste, effortlessly.',
      shop: 'Shop All Spirits',
      learn: 'Learn More',
      exploreHeading: 'Explore Our World',
      categories: { whisky: 'Whisky', gin: 'Gin', wine: 'Wine', beer: 'Beer' },
      featured: 'Featured Products',
      product1: { title: 'Nocturne Whiskey', price: '$89.99' },
      product2: { title: 'Starlight Gin', price: '$64.50' },
      product3: { title: 'Harvest Pinot Noir', price: '$55.00' },
      whyHeading: 'Crafted with Passion, Delivered with Care',
      whySub: 'We partner with independent distillers to bring you unique & high‑quality spirits.',
      whyButton: 'Learn More About Us',
      badge: 'Curated Excellence',
      shopPremium: 'Shop Premium Collection',
      exploreCategories: 'Explore Categories',
      stats: {
        customers: 'Happy Customers',
        spirits: 'Premium Spirits',
        rating: 'Customer Rating'
      },
      trust: {
        title: 'Trusted by 2,500+ customers worldwide',
        freeShipping: 'Free Shipping',
        securePayment: 'Secure Payment',
        easyReturns: 'Easy Returns'
      },
      featuredSubtitle: 'Handpicked selections from our master sommelier',
      badges: {
        bestSeller: 'Best Seller',
        new: 'New Arrival',
        limited: 'Limited Edition'
      },
      featuredCards: {
        whiskey: {
          category: 'Premium Whiskey',
          title: 'Nocturne Whiskey',
          desc: 'Aged 12 years in oak barrels with notes of vanilla and caramel',
          priceCurrent: '€89.99',
          priceOld: '€119.99'
        },
        gin: {
          category: 'Artisan Gin',
          title: 'Starlight Gin',
          desc: 'Botanical blend with juniper, lavender, and citrus notes',
          priceCurrent: '€64.50'
        },
        wine: {
          category: 'Fine Wine',
          title: 'Harvest Pinot Noir',
          desc: '2019 vintage from the Loire Valley with cherry undertones',
          priceCurrent: '€55.00'
        }
      },
      viewAllProducts: 'View All Products',
      newsletter: {
        title: 'Stay in the Spirit',
        subtitle: 'Get exclusive access to limited releases, expert recommendations, and special offers.',
        placeholder: 'Enter your email address',
        cta: 'Subscribe & Save 10%',
        privacyPrefix: 'No spam, unsubscribe anytime. See our',
        privacyLink: 'privacy policy'
      },
      why: {
        features: {
          curated: {
            title: 'Expertly Curated',
            desc: 'Every bottle hand-selected by our master sommelier'
          },
          craftsmanship: {
            title: 'Authentic Craftsmanship',
            desc: 'Direct partnerships with renowned distilleries'
          },
          limited: {
            title: 'Limited Editions',
            desc: 'Exclusive access to rare and vintage selections'
          }
        }
      },
      categoriesShowcase: {
        title: 'Discover Our Collections',
        subtitle: 'Explore our curated selection of premium beverages and snacks',
        items: {
          beer: {
            title: 'Beer',
            desc: 'Explore artisanal and premium beers from renowned breweries around the world',
            cta: 'Discover Beers'
          },
          wine: {
            title: 'Wine',
            desc: 'Distinguished wines from prestigious vineyards for every occasion',
            cta: 'Discover Wines'
          },
          whisky: {
            title: 'Whisky',
            desc: 'Premium whisky selections with rich flavors and exceptional character',
            cta: 'Discover Whiskies'
          },
          snacks: {
            title: 'Snacks',
            desc: 'Delightful appetizers and snacks perfect for pairing with your favorite drinks',
            cta: 'Discover Snacks'
          }
        }
      }
    },
    footer: {
      curated: 'Curated spirits delivered with care.',
      copyright: '© {year} Allo Soif. All rights reserved.',
      shop: ['Spirits', 'Collections', 'Gifts'],
      support: ['Contact', 'Shipping', 'Returns'],
      newsletterTitle: 'Join & Get 10% Off',
      emailPlaceholder: 'Email address',
      subscribe: 'Subscribe'
    },
    common: { 
      addToCart: 'Add to cart',
      removeFromCart: 'Remove from cart',
      emptyWishlist: 'Your wishlist is empty',
      emptyCart: 'Your cart is empty',
      inCart: 'In cart',
      removeFromWishlist: 'Remove from wishlist'
    },
    productsPage: {
      title: 'All Products',
      all: 'All',
      beer: 'Beer',
      wine: 'Wine',
      whisky: 'Whisky',
      snacks: 'Snacks',
      sortBy: 'Sort by',
      sort: {
        newest: 'Newest',
        priceAsc: 'Price: Low to High',
        priceDesc: 'Price: High to Low'
      },
      filters: {
        title: 'Filter',
        search: 'Search',
        searchPlaceholder: 'Search products',
        brewery: 'Brewery',
        color: 'Color',
        style: 'Style',
        country: 'Country',
        gamme: 'Range',
        abv: "ABV",
        container: 'Container',
        volume: 'Volume',
        abvRanges: {
          lt5: '< 5%',
          mid: '5% - 10%',
          gt10: '> 10%'
        }
      }
    },
    auth: {
      title: 'Welcome',
      haveAccount: 'Already have an account?',
      noAccount: "Don't have an account?",
      signIn: 'Sign in',
      createAccount: 'Create account',
      or: 'or',
      fields: {
        fullName: 'Full Name',
        email: 'Email',
        password: 'Password',
        phone: 'Phone number',
        address: 'Delivery address'
      },
      placeholders: {
        fullName: 'John Doe',
        email: 'you@example.com',
        password: '••••••••',
        phone: '+1 555 123 4567',
        address: 'Street, City, Country'
      },
      actions: {
        login: 'Login',
        register: 'Create account',
        loggingIn: 'Logging in...',
        registering: 'Creating account...',
        logout: 'Logout'
      },
      errors: {
        required: 'Please fill all required fields',
        generic: 'Something went wrong. Try again.'
      },
      success: {
        welcome: 'Welcome back!'
      }
    }
  },
  fr: {
    nav: {
      explore: 'Explorer',
      products: 'Produits',
      why: 'Pourquoi Nous',
      shop: 'Boutique',
      brandTitle: 'Allo Soif',
      brandSub: 'Spiritueux sélectionnés',
      login: 'Se connecter',
      logout: 'Se déconnecter',
      wishlist: 'Favoris',
      cart: 'Panier'
    },
    landing: {
      heading: 'Votre voyage commence ici : découvrez des spiritueux sélectionnés',
      sub: 'Des distilleries artisanales à votre porte. Une expérience gustative d’exception, sans effort.',
      shop: 'Voir tous les spiritueux',
      learn: 'En savoir plus',
      exploreHeading: 'Explorez notre monde',
      categories: { whisky: 'Whisky', gin: 'Gin', wine: 'Vin', beer: 'Bière' },
      featured: 'Produits en vedette',
      product1: { title: 'Whisky Nocturne', price: '89,99 $' },
      product2: { title: 'Gin Starlight', price: '64,50 $' },
      product3: { title: 'Pinot Noir Récolte', price: '55,00 $' },
      whyHeading: 'Conçu avec passion, livré avec soin',
      whySub: 'Nous travaillons avec des distillateurs indépendants pour vous offrir des spiritueux uniques et de qualité.',
      whyButton: 'En savoir plus sur nous',
      badge: 'Excellence sélectionnée',
      shopPremium: 'Voir la collection premium',
      exploreCategories: 'Explorer les catégories',
      stats: {
        customers: 'Clients satisfaits',
        spirits: 'Spiritueux premium',
        rating: 'Note clients'
      },
      trust: {
        title: 'Plébiscité par plus de 2 500 clients dans le monde',
        freeShipping: 'Livraison offerte',
        securePayment: 'Paiement sécurisé',
        easyReturns: 'Retours faciles'
      },
      featuredSubtitle: 'Sélection réalisée par notre sommelier',
      badges: {
        bestSeller: 'Meilleure vente',
        new: 'Nouvelle arrivée',
        limited: 'Édition limitée'
      },
      featuredCards: {
        whiskey: {
          category: 'Whisky premium',
          title: 'Whisky Nocturne',
          desc: 'Vieilli 12 ans en fûts de chêne avec des notes de vanille et de caramel',
          priceCurrent: '89,99 €',
          priceOld: '119,99 €'
        },
        gin: {
          category: 'Gin artisanal',
          title: 'Gin Starlight',
          desc: 'Assemblage botanique avec genièvre, lavande et agrumes',
          priceCurrent: '64,50 €'
        },
        wine: {
          category: 'Vin d’exception',
          title: 'Pinot Noir Récolte',
          desc: 'Millésime 2019 de la vallée de la Loire, aux notes de cerise',
          priceCurrent: '55,00 €'
        }
      },
      viewAllProducts: 'Voir tous les produits',
      newsletter: {
        title: 'Restez dans l’esprit',
        subtitle: 'Accédez en exclusivité aux éditions limitées, recommandations et offres spéciales.',
        placeholder: 'Entrez votre adresse e‑mail',
        cta: 'S’abonner et économiser 10 %',
        privacyPrefix: 'Pas de spam, désinscription à tout moment. Voir notre',
        privacyLink: 'politique de confidentialité'
      },
      why: {
        features: {
          curated: {
            title: 'Sélection experte',
            desc: 'Chaque bouteille est choisie par notre sommelier'
          },
          craftsmanship: {
            title: 'Savoir‑faire authentique',
            desc: 'Partenariats directs avec des distilleries renommées'
          },
          limited: {
            title: 'Éditions limitées',
            desc: 'Accès exclusif à des cuvées rares et millésimées'
          }
        }
      },
      categoriesShowcase: {
        title: 'Découvrez nos collections',
        subtitle: 'Explorez notre sélection de boissons et d’amuse‑gueules premium',
        items: {
          beer: {
            title: 'Bière',
            desc: 'Découvrez des bières artisanales et premium de brasseries renommées dans le monde',
            cta: 'Découvrir les bières'
          },
          wine: {
            title: 'Vin',
            desc: 'Des vins d’exception issus de vignobles prestigieux pour toutes les occasions',
            cta: 'Découvrir les vins'
          },
          whisky: {
            title: 'Whisky',
            desc: 'Sélection de whiskies premium aux arômes riches et au caractère exceptionnel',
            cta: 'Découvrir les whiskies'
          },
          snacks: {
            title: 'Amuse‑gueules',
            desc: 'De délicieux accompagnements parfaits avec vos boissons préférées',
            cta: 'Découvrir les amuse‑gueules'
          }
        }
      }
    },
    footer: {
      curated: 'Spiritueux sélectionnés, livrés avec soin.',
      copyright: '© {year} Allo Soif. Tous droits réservés.',
      shop: ['Spiritueux', 'Collections', 'Cadeaux'],
      support: ['Contact', 'Expédition', 'Retours'],
      newsletterTitle: 'Inscrivez‑vous & obtenez 10% de réduction',
      emailPlaceholder: 'Adresse e‑mail',
      subscribe: 'S’abonner'
    },
    common: { 
      addToCart: 'Ajouter au panier',
      removeFromCart: 'Retirer du panier',
      emptyWishlist: 'Votre liste de favoris est vide',
      emptyCart: 'Votre panier est vide',
      inCart: 'Dans le panier',
      removeFromWishlist: 'Retirer des favoris'
    },
    productsPage: {
      title: 'Tous les produits',
      all: 'Tous',
      beer: 'Bière',
      wine: 'Vin',
      whisky: 'Whisky',
      snacks: 'Amuse‑gueules',
      sortBy: 'Trier par',
      sort: {
        newest: 'Nouveautés',
        priceAsc: 'Prix croissant',
        priceDesc: 'Prix décroissant'
      },
      filters: {
        title: 'Filtrer',
        search: 'Recherche',
        searchPlaceholder: 'Rechercher dans les produits',
        brewery: 'Brasserie',
        color: 'Couleur',
        style: 'Style',
        country: 'Pays',
        gamme: 'Gamme',
        abv: "Degré d'alcool",
        container: 'Contenant',
        volume: 'Volume',
        abvRanges: {
          lt5: '< 5%',
          mid: '5% - 10%',
          gt10: '> 10%'
        }
      }
    },
    auth: {
      title: 'Bienvenue',
      haveAccount: 'Vous avez déjà un compte ?',
      noAccount: "Vous n'avez pas de compte ?",
      signIn: 'Se connecter',
      createAccount: 'Créer un compte',
      or: 'ou',
      fields: {
        fullName: 'Nom complet',
        email: 'Email',
        password: 'Mot de passe',
        phone: 'Numéro de téléphone',
        address: 'Adresse de livraison'
      },
      placeholders: {
        fullName: 'Jean Dupont',
        email: 'vous@exemple.com',
        password: '••••••••',
        phone: '+33 6 12 34 56 78',
        address: 'Rue, Ville, Pays'
      },
      actions: {
        login: 'Se connecter',
        register: 'Créer un compte',
        loggingIn: 'Connexion...',
        registering: 'Création...',
        logout: 'Se déconnecter'
      },
      errors: {
        required: 'Veuillez remplir tous les champs obligatoires',
        generic: 'Une erreur est survenue. Réessayez.'
      },
      success: {
        welcome: 'Ravi de vous revoir !'
      }
    }
  }
};

export const LocaleContext = createContext({
  locale: 'en',
  setLocale: () => {},
  t: (k) => k
});

export function LocaleProvider({ children }) {
  const [locale, setLocale] = useState(() => {
    try {
      return localStorage.getItem('locale') || 'en';
    } catch {
      return 'en';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('locale', locale);
    } catch {}
  }, [locale]);

  const t = (key, vars = {}) => {
    const parts = key.split('.');
    let cur = translations[locale];
    for (const p of parts) {
      if (!cur) return key;
      cur = cur[p];
    }
    if (typeof cur !== 'string') return key;
    // simple placeholder replacement {name}
    return Object.keys(vars).reduce((str, k) => str.replace(new RegExp(`\\{${k}\\}`, 'g'), vars[k]), cur);
  };

  const value = useMemo(() => ({ locale, setLocale, t }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/* ----------------- Auth: Context + Provider with Modal UI ------------------ */
const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000/api';

export const AuthContext = createContext({
  user: null,
  token: null,
  login: async () => {},
  register: async () => {},
  logout: () => {},
  openAuth: () => {},
  closeAuth: () => {},
  loading: false
});

export function AuthProvider({ children }) {
  const { t } = useContext(LocaleContext);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true); // Add initial loading state
  const [error, setError] = useState('');
  const [showAuth, setShowAuth] = useState(false);
  const [mode, setMode] = useState('login'); // 'login' | 'register'

  // Initialize auth state on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const saved = localStorage.getItem('auth');
        if (saved) {
          const { token: savedToken, user: savedUser } = JSON.parse(saved);
          if (savedToken && savedUser) {
            // Validate token by making a request to /me endpoint
            try {
              const response = await fetch(`${API_BASE}/auth/me`, {
                headers: {
                  'Authorization': `Bearer ${savedToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (response.ok) {
                const data = await response.json();
                setToken(savedToken);
                setUser(data.user || savedUser);
              } else {
                // Token is invalid, clear storage
                localStorage.removeItem('auth');
              }
            } catch (error) {
              // Network error or server down - keep offline auth for better UX
              console.warn('Auth validation failed, using cached auth:', error.message);
              setToken(savedToken);
              setUser(savedUser);
            }
          }
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        localStorage.removeItem('auth');
      } finally {
        setInitializing(false);
      }
    };

    initializeAuth();
  }, []);

  // Persist auth state whenever it changes
  useEffect(() => {
    if (!initializing) {
      try {
        if (token && user) {
          localStorage.setItem('auth', JSON.stringify({ token, user }));
        } else {
          localStorage.removeItem('auth');
        }
      } catch (error) {
        console.error('Failed to persist auth state:', error);
      }
    }
  }, [token, user, initializing]);

  const request = async (path, body, method = 'POST') => {
    const res = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: method === 'GET' ? undefined : JSON.stringify(body || {})
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = data?.message || data?.error || t('auth.errors.generic');
      throw new Error(msg);
    }
    return data;
  };

  const login = async (email, password) => {
    setError('');
    setLoading(true);
    try {
      const data = await request('/auth/login', { email, password });
      const nextToken = data.token;
      const nextUser = data.user || { email: data.email, fullName: data.fullName };
      if (!nextToken) throw new Error('No token');
      setToken(nextToken);
      setUser(nextUser);
      setShowAuth(false);
      return true;
    } catch (e) {
      setError(e.message || t('auth.errors.generic'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const register = async (form) => {
    setError('');
    setLoading(true);
    try {
      const data = await request('/auth/register', form);
      // many APIs return token+user on register; if not, auto-login
      const nextToken = data.token;
      const nextUser = data.user || { fullName: form.fullName, email: form.email, phone: form.phone, address: form.address };
      if (nextToken) {
        setToken(nextToken);
        setUser(nextUser);
        setShowAuth(false);
        return true;
      }
      // fallback: login after registration
      await login(form.email, form.password);
      return true;
    } catch (e) {
      setError(e.message || t('auth.errors.generic'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    try { 
      localStorage.removeItem('auth'); 
    } catch (error) {
      console.error('Failed to clear auth storage:', error);
    }
  };

  const openAuth = (nextMode = 'login') => {
    setMode(nextMode);
    setShowAuth(true);
    setError('');
  };
  const closeAuth = () => setShowAuth(false);

  const value = useMemo(() => ({
    user, token, login, register, logout, openAuth, closeAuth, loading, error, initializing
  }), [user, token, loading, error, initializing]);

  // Show loading screen while initializing auth
  if (initializing) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--text)',
        zIndex: 9999
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16
        }}>
          <div style={{
            width: 40,
            height: 40,
            border: '3px solid rgba(212,154,106,0.3)',
            borderTop: '3px solid var(--accent)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div>Loading...</div>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showAuth && (
        <div className="modal-backdrop" onClick={closeAuth} role="dialog" aria-modal="true">
          <div className="modal auth-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{t('auth.title')}</div>
              <button className="modal-close" onClick={closeAuth} aria-label="close">×</button>
            </div>
            <div className="modal-body">
              <AuthForm
                mode={mode}
                setMode={setMode}
                loading={loading}
                error={error}
                onLogin={login}
                onRegister={register}
                t={t}
              />
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

function AuthForm({ mode, setMode, loading, error, onLogin, onRegister, t }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    await onLogin(email, password);
  };
  const handleRegister = async (e) => {
    e.preventDefault();
    if (!fullName || !email || !password) return;
    await onRegister({ fullName, email, password, phone, address });
  };

  return (
    <div className="auth-forms">
      <div className="tabs">
        <button
          className={`tab ${mode === 'login' ? 'active' : ''}`}
          onClick={() => setMode('login')}
        >
          {t('auth.signIn')}
        </button>
        <button
          className={`tab ${mode === 'register' ? 'active' : ''}`}
          onClick={() => setMode('register')}
        >
          {t('auth.createAccount')}
        </button>
      </div>

      {mode === 'login' ? (
        <form onSubmit={handleLogin} className="auth-form">
          <label>
            {t('auth.fields.email')}
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.placeholders.email')}
              required
            />
          </label>
          <label>
            {t('auth.fields.password')}
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('auth.placeholders.password')}
              required
            />
          </label>
          {error ? <div className="form-error">{error}</div> : null}
          <button className="btn" type="submit" disabled={loading}>
            {loading ? t('auth.actions.loggingIn') : t('auth.actions.login')}
          </button>
        </form>
      ) : (
        <form onSubmit={handleRegister} className="auth-form">
          <div className="grid-2">
            <label>
              {t('auth.fields.fullName')}
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('auth.placeholders.fullName')}
                required
              />
            </label>
            <label>
              {t('auth.fields.email')}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.placeholders.email')}
                required
              />
            </label>
          </div>
          <div className="grid-2">
            <label>
              {t('auth.fields.phone')}
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('auth.placeholders.phone')}
              />
            </label>
            <label>
              {t('auth.fields.password')}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.placeholders.password')}
                required
              />
            </label>
          </div>
          <label>
            {t('auth.fields.address')}
            <input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t('auth.placeholders.address')}
            />
          </label>
          {error ? <div className="form-error">{error}</div> : null}
          <button className="btn" type="submit" disabled={loading}>
            {loading ? t('auth.actions.registering') : t('auth.actions.register')}
          </button>
        </form>
      )}
    </div>
  );
}

// Add WishlistContext
export const WishlistContext = React.createContext({
  wishlist: [],
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  isInWishlist: () => false,
});

// Provider for wishlist
export function WishlistProvider({ children }) {
  const { user, token, initializing: authInitializing } = useContext(AuthContext);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUserId, setLastUserId] = useState(null);

  // Initialize wishlist when auth state changes
  useEffect(() => {
    const initializeWishlist = async () => {
      if (authInitializing) {
        return; // Wait for auth to finish initializing
      }

      const currentUserId = user?.id || user?.email || null;
      
      // Skip if we already initialized for this user
      if (currentUserId === lastUserId) {
        return;
      }
      
      setLastUserId(currentUserId);

      if (user && token) {
        // User is logged in - try to load from backend, fallback to localStorage
        await loadWishlistFromBackend();
      } else {
        // Guest user - load from localStorage
        try {
          const saved = JSON.parse(localStorage.getItem('wishlist') || '[]');
          setWishlist(saved);
        } catch {
          setWishlist([]);
        }
      }
    };

    initializeWishlist();
  }, [user, token, authInitializing, lastUserId]);

  // Save to localStorage for guest users only
  useEffect(() => {
    if (!user) {
      try {
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
      } catch (error) {
        console.error('Failed to save wishlist to localStorage:', error);
      }
    }
  }, [wishlist, user]);

  const loadWishlistFromBackend = async () => {
    if (!token) {
      return;
    }
    
    setLoading(true);
    try {
      const healthResponse = await fetch(`${API_BASE}/health`);
      if (!healthResponse.ok) {
        throw new Error('Backend server not available');
      }
      
      const response = await fetch(`${API_BASE}/auth/wishlist`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const backendWishlist = data.wishlist || [];
        
        let localWishlist = [];
        try {
          localWishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
        } catch {
          localWishlist = [];
        }
        
        const mergedWishlist = [...backendWishlist];
        localWishlist.forEach(item => {
          if (!mergedWishlist.some(w => w.id === item.id)) {
            mergedWishlist.push(item);
          }
        });
        
        setWishlist(mergedWishlist);
        
        if (mergedWishlist.length > backendWishlist.length) {
          await syncWishlistToBackend(mergedWishlist);
        }
        
        if (localWishlist.length > 0) {
          localStorage.removeItem('wishlist');
        }
      } else {
        throw new Error(`Backend responded with ${response.status}`);
      }
    } catch (error) {
      console.warn('Backend not available or error loading wishlist, using localStorage:', error.message);
      
      try {
        const saved = JSON.parse(localStorage.getItem('wishlist') || '[]');
        setWishlist(saved);
        
        if (saved.length > 0) {
          console.info?.('Your wishlist is temporarily stored locally. It will sync when the server is available.');
        }
      } catch {
        setWishlist([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const syncWishlistToBackend = async (newWishlist) => {
    if (!token || !user) {
      return;
    }
    
    try {
      const healthResponse = await fetch(`${API_BASE}/health`);
      if (!healthResponse.ok) {
        localStorage.setItem('wishlist', JSON.stringify(newWishlist));
        return;
      }
      
      const response = await fetch(`${API_BASE}/auth/wishlist`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ wishlist: newWishlist })
      });
      
      if (!response.ok) {
        throw new Error(`Sync failed with status ${response.status}`);
      } else {
        localStorage.removeItem('wishlist');
      }
    } catch (error) {
      console.warn('Failed to sync wishlist to backend, storing locally:', error.message);
      localStorage.setItem('wishlist', JSON.stringify(newWishlist));
    }
  };

  const addToWishlist = async (item) => {
    if (wishlist.some((p) => p.id === item.id)) {
      return; // Item already in wishlist
    }
    
    const newWishlist = [...wishlist, item];
    setWishlist(newWishlist);
    
    if (user && token) {
      await syncWishlistToBackend(newWishlist);
    }
  };

  const removeFromWishlist = async (id) => {
    const newWishlist = wishlist.filter((p) => p.id !== id);
    setWishlist(newWishlist);
    
    if (user && token) {
      await syncWishlistToBackend(newWishlist);
    }
  };

  const isInWishlist = (id) => wishlist.some((p) => p.id === id);

  const value = useMemo(
    () => ({ wishlist, addToWishlist, removeFromWishlist, isInWishlist, loading }),
    [wishlist, loading]
  );

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

// Add CartContext after WishlistContext
export const CartContext = React.createContext({
  cart: [],
  addToCart: () => {},
  removeFromCart: () => {},
  isInCart: () => false,
  getCartTotal: () => 0,
});

// Provider for cart
export function CartProvider({ children }) {
  const [cart, setCart] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('cart') || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch {}
  }, [cart]);

  const addToCart = (item) => {
    setCart((prev) => {
      const existing = prev.find(p => p.id === item.id);
      if (existing) {
        return prev.map(p => p.id === item.id ? { ...p, quantity: p.quantity + 1 } : p);
      }
      return [...prev, { ...item, quantity: 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCart((prev) => prev.filter((p) => p.id !== id));
  };

  const isInCart = (id) => cart.some((p) => p.id === id);

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const value = useMemo(
    () => ({ cart, addToCart, removeFromCart, isInCart, getCartTotal }),
    [cart]
  );

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}